import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  DataTable,
  Pagination,
  Select,
  Spinner,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAdmin } from "../../../context/admin";
import { fetchData } from "../../../util/fetch";

const PosChangeSummary = ({ importHistory, onRevertSelected }) => {
  const { selectedShopId } = useAdmin();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  });
  const [search, setSearch] = useState("");
  const [changeTypeFilter, setChangeTypeFilter] = useState("all");

  // Selection state
  const [selectedGids, setSelectedGids] = useState(new Set());

  const isUnmounted = useRef(false);
  const importHistoryId = importHistory?.id;

  const loadChanges = useCallback(
    async (page = 1) => {
      if (!selectedShopId || !importHistoryId) return;

      try {
        setLoading(true);
        let url = `/api/shops/${selectedShopId}/pos_import_histories/${importHistoryId}/changes?page=${page}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (changeTypeFilter !== "all")
          url += `&change_type=${changeTypeFilter}`;

        fetchData(
          url,
          (response) => {
            if (!isUnmounted.current) {
              setChanges(response.changes || []);
              setPagination(response.pagination || {});
              setLoading(false);
              // Reset selection on new data load (simplest approach)
              setSelectedGids(new Set());
            }
          },
          (err) => {
            if (!isUnmounted.current) {
              setError("Failed to load changes");
              console.error(err);
              setLoading(false);
            }
          },
        );
      } catch (err) {
        if (!isUnmounted.current) {
          setError("Failed to load changes");
          console.error(err);
          setLoading(false);
        }
      }
    },
    [selectedShopId, importHistoryId, search, changeTypeFilter],
  );

  useEffect(() => {
    loadChanges(1);
  }, [loadChanges]);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);

  const handleSearchChange = useCallback((value) => setSearch(value), []);
  const handleChangeTypeFilterChange = useCallback(
    (value) => setChangeTypeFilter(value),
    [],
  );

  const handleSelectionChange = (gid, checked) => {
    const newSelected = new Set(selectedGids);
    if (checked) {
      newSelected.add(gid);
    } else {
      newSelected.delete(gid);
    }
    setSelectedGids(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const pageGids = changes.map((c) => c.product_gid).filter(Boolean);
      const newSelected = new Set(selectedGids);
      pageGids.forEach((gid) => newSelected.add(gid));
      setSelectedGids(newSelected);
    } else {
      // Deselect all on current page
      const pageGids = changes.map((c) => c.product_gid).filter(Boolean);
      const newSelected = new Set(selectedGids);
      pageGids.forEach((gid) => newSelected.delete(gid));
      setSelectedGids(newSelected);
    }
  };

  // Determine "Select All" state for current page
  const pageGids = changes.map((c) => c.product_gid).filter(Boolean);
  const uniquePageGids = new Set(pageGids);
  const allSelected =
    uniquePageGids.size > 0 &&
    Array.from(uniquePageGids).every((gid) => selectedGids.has(gid));
  const someSelected =
    uniquePageGids.size > 0 &&
    !allSelected &&
    Array.from(uniquePageGids).some((gid) => selectedGids.has(gid));

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const getChangeTypeBadge = (type) => {
    if (!type) return <Badge status="info">UNKNOWN</Badge>;
    const tones = {
      status: "info",
      price: "warning",
      title: "critical",
      tags: "success",
      product_type: "attention",
      draft: "new",
    };
    return (
      <Badge status={tones[type] || "info"}>
        {type.toString().toUpperCase()}
      </Badge>
    );
  };

  const rows = changes.map((change) => {
    const isSelected =
      change.product_gid && selectedGids.has(change.product_gid);
    return [
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onChange={(newVal) =>
            handleSelectionChange(change.product_gid, newVal)
          }
          disabled={!change.product_gid}
        />
      </div>,
      <TextStyle variation="strong">{change.sku || "-"}</TextStyle>,
      <div style={{ maxWidth: "200px", whiteSpace: "normal" }}>
        {change.product_title || ""}
      </div>,
      getChangeTypeBadge(change.change_type),
      <TextStyle variation="code">{change.field || ""}</TextStyle>,
      <div style={{ color: "#d82c0d" }}>{formatValue(change.old_value)}</div>,
      <div style={{ color: "#008060" }}>{formatValue(change.new_value)}</div>,
    ];
  });

  if (loading && pagination.current_page === 1) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Section>
        <Stack alignment="center">
          <div style={{ flex: 1 }}>
            <TextField
              label="Search"
              labelHidden
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by SKU or Product Title"
              autoComplete="off"
            />
          </div>
          <Select
            label="Change Type"
            labelHidden
            options={[
              { label: "All Types", value: "all" },
              { label: "Status", value: "status" },
              { label: "Price", value: "price" },
              { label: "Title", value: "title" },
              { label: "Tags", value: "tags" },
              { label: "Product Type", value: "product_type" },
            ]}
            onChange={handleChangeTypeFilterChange}
            value={changeTypeFilter}
          />
        </Stack>
      </Card.Section>

      {/* Bulk Actions Bar */}
      <div
        style={{
          padding: "10px 16px",
          background: "#f4f6f8",
          borderBottom: "1px solid #dfe3e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack alignment="center">
          <Checkbox
            label={`${selectedGids.size} selected`}
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
          />
          {selectedGids.size > 0 && (
            <Button
              size="slim"
              destructive
              onClick={() =>
                onRevertSelected && onRevertSelected(Array.from(selectedGids))
              }
            >
              Revert {selectedGids.size} Selected Products
            </Button>
          )}
        </Stack>
      </div>

      {error && (
        <Card.Section>
          <Banner status="critical">{error}</Banner>
        </Card.Section>
      )}

      <DataTable
        columnContentTypes={[
          "text",
          "text",
          "text",
          "text",
          "text",
          "text",
          "text",
        ]}
        headings={[
          "", // Checkbox column
          "SKU",
          "Product",
          "Type",
          "Field",
          "Old Value",
          "New Value",
        ]}
        rows={rows}
        footerContent={`Showing ${changes.length} of ${pagination.total_count} changes`}
      />

      {pagination.total_pages > 1 && (
        <div
          style={{
            padding: "16px",
            display: "flex",
            justifyContent: "center",
            borderTop: "1px solid #dfe3e8",
          }}
        >
          <Pagination
            hasPrevious={pagination.current_page > 1}
            onPrevious={() => loadChanges(pagination.current_page - 1)}
            hasNext={pagination.current_page < pagination.total_pages}
            onNext={() => loadChanges(pagination.current_page + 1)}
            label={`Page ${pagination.current_page} of ${pagination.total_pages}`}
          />
        </div>
      )}
    </Card>
  );
};

export default PosChangeSummary;
